import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { switchMap } from 'rxjs';
import {
  AutoscalingProfileOverridesValue,
  ServiceStack,
  ServiceStackAutoscaling,
  ServiceStackBackupConfig,
  ServiceStackBackupResponse,
  ProjectBackupListResponse,
  ServiceStackPort,
  ServiceStackUiAccess,
  ServiceStackUpdateData,
  TriggerPipelinePayload
} from '@zerops/models/service-stack';
import { Process } from '@zerops/models/process';
import { normalizeApiUrl } from '@zerops/zef/utils';
import { ContainerFilesAccess, ContainerFile } from './service-stack-base.model';
import { PipelineDebugModes, PipelineDebugPhases } from './service-stack-base.constant';

@Injectable({ providedIn: 'root' })
export class ServiceStackBaseApi {

  constructor(private _http: HttpClient) { }

  getContainerFile$(serviceStackId: string, containerId: string, filePath: string) {
    return this._http.post<ContainerFilesAccess>(
      `/api/service-stack/${serviceStackId}/file-browsing-access`,
      { containerId }
    ).pipe(
      switchMap(({ readFileUrl }) => this._http.get<ContainerFile>(
        `${normalizeApiUrl(readFileUrl)}&containerId=${containerId}&path=${filePath}`
      ))
    )
  }

  add$(projectId: string, serviceVersion: string, data: any) {
    // Object storage uses its own legacy endpoint and payload shape
    // (diskGBytes, policy, rawPolicy, cdnEnabled) which the generic endpoint doesn't support.
    if (serviceVersion === 'object-storage') {
      return this._http.post<ServiceStack & { process: Process; }>(
        `/api/project/${projectId}/service-stack/object_storage_v1`,
        data
      );
    }

    return this._http.post<ServiceStack & { process: Process; }>(
      `/api/project/${projectId}/service-stack`,
      {
        serviceStackVersionName: serviceVersion,
        ...data
      }
    );
  }

  uiAccess$(id: string) {
    return this._http.post<ServiceStackUiAccess>(
      `/api/service-stack/${id}/ui-access`,
      {}
    );
  }

  enableSubdomainAccess$(id: string) {
    return this._http.put<Process>(
      `/api/service-stack/${id}/enable-subdomain-access`,
      {}
    );
  }

  disableSubdomainAccess$(id: string) {
    return this._http.put<Process>(
      `/api/service-stack/${id}/disable-subdomain-access`,
      {}
    );
  }

  // -- start, stop, restart, reload
  triggerServiceStackOperation$(id: string, type: string) {
    return this._http.put<Process>(
      `/api/service-stack/${id}/${type}`,
      {}
    );
  }

  // -- internal ports
  modifyInternalPorts$(id: string, ports: ServiceStackPort[]) {
    return this._http.put<Process>(
      `/api/service-stack/${id}/ports`,
      {
        ports
      }
    );
  }

  // -- update object storage size
  updateObjectStorageSettings$(
    serviceStackId: string,
    diskGBytes: number,
    policy?: string,
    rawPolicy?: string,
    cdnEnabled?: boolean
  ) {
    return this._http.put<Process>(
      `/api/service-stack/${serviceStackId}/object-storage-size`,
      {
        diskGBytes,
        policy,
        rawPolicy,
        cdnEnabled
      }
    );
  }

  // -- connect / disconnect with external repository
  externalRepositoryIntegration$(id: string, data: {
    gitLabIntegration?: any;
    gitHubIntegration?: any;
  }) {
    return this._http.put<{ success: boolean; }>(
      `/api/service-stack/${id}/external-repository-integration`,
      data
    );
  }

  // -- stack update
  stackUpdate$(id: string, typeVersion: string, data: ServiceStackUpdateData) {
    return this._http.put<Process>(
      `/api/service-stack/${typeVersion}/${id}`,
      data
    );
  }

  serviceStackExport$(id: string, reveal?: boolean) {
    return this._http.get<{ yaml: string; }>(
      `/api/service-stack/${id}/export${reveal ? '?reveal=true' : ''}`
    );
  }

  serviceStackImport$(
    yaml: string,
    projectId: string,
    recipeSource?: string,
    recipeSourceUrl?: string
  ) {
    return this._http.post<{ projectId: string; projectName: string; serviceStacks: ServiceStack[]; }>(
      `/api/project/${projectId}/service-stack/import`,
      {
        yaml,
        projectId,
        ...(recipeSource && { recipeSource }),
        ...(recipeSourceUrl && { recipeSourceUrl })
      }
    );
  }

  /**
   * First-class recipe import for development containers (zagent/zcde).
   * Backend handles: token creation, ZCP_API_KEY env var, sshIsolation, and YAML import atomically.
   */
  firstClassRecipeImport$(
    projectId: string,
    serviceImportYaml: string,
    recipeSource?: string,
    recipeSourceUrl?: string,
    createIntegrationToken?: boolean
  ) {
    return this._http.put<{ projectId: string; projectName: string; processes: Process[]; }>(
      `/api/project/${projectId}/first-class-recipe/development-container`,
      {
        serviceImportYaml,
        ...(recipeSource && { recipeSource }),
        ...(recipeSourceUrl && { recipeSourceUrl }),
        ...(createIntegrationToken !== undefined && { createIntegrationToken })
      }
    );
  }

  adjustAutoscaling$(
    serviceStackId: string,
    customAutoscaling: ServiceStackAutoscaling,
    autoscalingProfileId?: string,
    autoscalingProfileOverrides?: AutoscalingProfileOverridesValue | null
  ) {
    return this._http.put<{ process: Process }>(
      `/api/service-stack/${serviceStackId}/autoscaling`,
      {
        autoscalingProfileId,
        customAutoscaling,
        ...(autoscalingProfileOverrides !== undefined && {
          autoscalingProfileOverrides: autoscalingProfileOverrides ?? null
        })
      }
    );
  }

  deleteBuildCache$(id: string) {
    return this._http.delete<{ success: boolean; }>(
      `/api/service-stack/${id}/build-cache`
    );
  }

  deleteRuntimePrepareCache$(id: string) {
    return this._http.put<{ success: boolean; }>(
      `/api/service-stack/${id}/next-app-version`,
      { useCustomRuntime: false }
    );
  }

  getBackups$(id: string) {
    return this._http.get<ServiceStackBackupResponse>(
      `/api/service-stack/${id}/backup`
    );
  }

  setBackupConfig$(id: string, config: ServiceStackBackupConfig) {
    return this._http.put<{ process: Process; }>(
      `/api/service-stack/${id}/backup-config`,
      config
    );
  }

  createBackup$(id: string, tags?: string[]) {
    return this._http.post<{ success: boolean; async: boolean; }>(
      `/api/service-stack/${id}/backup`,
      { tags }
    );
  }

  getProjectBackups$(projectId: string, params: { limit?: number; offset?: number; serviceStackId?: string; sort?: string[]; } = {}) {
    return this._http.get<ProjectBackupListResponse>(
      `/api/project/${projectId}/backup`,
      { params: { ...params } }
    );
  }

  deleteBackup$(projectId: string, serviceStackId: string, name: string) {
    return this._http.delete<{ success: boolean; }>(
      `/api/project/${projectId}/backup/${serviceStackId}/${name}`
    );
  }

  downloadUrlBackup$(projectId: string, serviceStackId: string, name: string) {
    return this._http.post<{ url: string }>(
      `/api/project/${projectId}/backup/download-url/${serviceStackId}/${name}`,
      {}
    );
  }

  pipelineDebugMode$(id: string, phase: PipelineDebugPhases, mode: PipelineDebugModes) {
    return this._http.put<Record<PipelineDebugPhases, PipelineDebugModes>>(
      `/api/service-stack/${id}/debug`,
      { [phase]: mode }
    );
  }

  triggerRedeploy$(id: string, envVarsFile?: string, envSecretsFile?: string) {
    return this._http.put<{ process: Process; }>(
      `/api/service-stack/${id}/trigger-external-repository-integration`,
      { envVarsFile, envSecretsFile }
    );
  }

  triggerPipeline$(id: string, data: TriggerPipelinePayload) {
    return this._http.put<{ process: Process; }>(
      `/api/service-stack/${id}/trigger-pipeline`,
      data
    );
  }

}
